import React, { useState, useEffect } from 'react';
import { LogOut, LayoutGrid, Plus, FolderGit2, FileText, Wand2, Layout, FileCode2, UploadCloud, Sparkles, Link as LinkIcon } from 'lucide-react';
import { ClientEntity, Showcase } from '../../types';
import { PortfoliosList } from './PortfoliosList';
import { ShowcaseBuilder } from './builder/ShowcaseBuilder';
import { ShowcaseEditor } from './editor/ShowcaseEditor';
import { db } from '../../services/db';
import { toast } from 'sonner';

interface ShowcaseDashboardProps {
  clients: ClientEntity[];
  onLogout: () => void;
}

export const ShowcaseDashboard: React.FC<ShowcaseDashboardProps> = ({ clients, onLogout }) => {
  // Top-Level Horizontal Menu State
  const [activeSystem, setActiveSystem] = useState<'showcase' | 'proposals' | 'documents'>('showcase');

  // Showcase Sub-System State
  const [activeView, setActiveView] = useState<'list' | 'create' | 'edit'>('list');
  const [editShowcaseId, setEditShowcaseId] = useState<string | null>(null);
  const [showcases, setShowcases] = useState<Showcase[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Proposals Sub-System State (Placeholder)
  const [proposalView, setProposalView] = useState<'list' | 'templates'>('list');

  // Documents Sub-System State
  const [documentView, setDocumentView] = useState<'list' | 'upload' | 'upload_classic' | 'dynamic_editor' | 'smart_template' | 'visual_editor' | 'ai_generator'>('list');
  const [documentLibraryFilter, setDocumentLibraryFilter] = useState<'all' | 'proposals' | 'mediakits' | 'presentations' | 'links'>('all');


  const loadShowcases = async () => {
    setIsLoading(true);
    try {
      const data = await db.getShowcases();
      setShowcases(data);
    } catch (err) {
      toast.error('Erro ao carregar portfólios.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadShowcases();
  }, []);

  const handleSaveShowcase = async (showcase: Showcase) => {
    try {
      await db.saveShowcase(showcase);
      toast.success('Vitrine salva com sucesso!');
      await loadShowcases();
      setActiveView('list');
    } catch (err: any) {
      console.error("Save Error:", err);
      toast.error(`Erro ao salvar: ${err.message || 'Falha na comunicação com a nuvem'}`);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await db.deleteShowcase(id);
      await loadShowcases();
      toast.success('Vitrine excluída.');
    } catch (err) {
      toast.error('Erro ao excluir vitrine.');
    }
  };

  return (
    <div className="flex flex-col h-screen bg-[#FAFAFA] text-gray-900 font-sans overflow-hidden">
      
      {/* HORIZONTAL TOP MENU */}
      <header className="h-14 border-b border-gray-200 bg-white flex items-center px-6 shrink-0 z-30">
        <div className="flex items-center gap-3 mr-8">
            <div className="w-6 h-6 bg-black rounded-full flex items-center justify-center">
               <span className="text-white font-bold text-[10px]">NY</span>
            </div>
            <span className="text-gray-300 pointer-events-none select-none">/</span>
            <div className="flex items-center gap-2">
              <span className="text-[14px] font-medium text-gray-900">Material Workspace</span>
              <span className="px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[10px] font-medium">BETA</span>
            </div>
        </div>
        
        <nav className="flex gap-6 h-full items-center ml-4">
           <button 
             onClick={() => setActiveSystem('showcase')}
             className={`h-full flex items-center px-1 text-[14px] font-medium transition-colors border-b-[2px] ${activeSystem === 'showcase' ? 'border-black text-black' : 'border-transparent text-gray-500 hover:text-gray-900'}`}
           >
             Portfólios
           </button>
           <button 
             onClick={() => setActiveSystem('proposals')}
             className={`h-full flex items-center px-1 text-[14px] font-medium transition-colors border-b-[2px] ${activeSystem === 'proposals' ? 'border-black text-black' : 'border-transparent text-gray-500 hover:text-gray-900'}`}
           >
             Propostas
           </button>
           <button 
             onClick={() => setActiveSystem('documents')}
             className={`h-full flex items-center px-1 text-[14px] font-medium transition-colors border-b-[2px] ${activeSystem === 'documents' ? 'border-black text-black' : 'border-transparent text-gray-500 hover:text-gray-900'}`}
           >
             PDFs e Materiais
           </button>
        </nav>

        <div className="ml-auto">
          <button onClick={onLogout} className="text-[14px] font-medium text-gray-500 hover:text-black flex items-center gap-2 transition-colors">
            <LogOut size={16} />
            <span className="hidden sm:inline">Sair</span>
          </button>
        </div>
      </header>

      {/* DYNAMIC SUB-SYSTEMS WORKSPACE */}
      <div className="flex-1 flex overflow-hidden">
        
        {/* MICRO-SYSTEM 1: SHOWCASE ENGINE */}
        {activeSystem === 'showcase' && (
          <>
            {/* Contextual Sidebar */}
            <aside className="w-[220px] bg-[#FAFAFA] border-r border-gray-200 flex flex-col shrink-0 z-20">
              <div className="flex-1 py-6 px-4 flex flex-col gap-1 overflow-y-auto">
                 <button 
                   onClick={() => setActiveView('list')}
                   className={`flex items-center gap-2.5 px-3 py-2 rounded-md text-[14px] font-medium transition-all ${activeView === 'list' ? 'bg-gray-200/50 text-gray-900' : 'text-gray-600 hover:bg-gray-100/50 hover:text-gray-900'}`}
                 >
                   <FolderGit2 size={16} strokeWidth={2} className={activeView === 'list' ? 'text-gray-900' : 'text-gray-500'} />
                   Portfólios
                 </button>
                 <button 
                   onClick={() => setActiveView('create')}
                   className={`flex items-center gap-2.5 px-3 py-2 rounded-md text-[14px] font-medium transition-all ${activeView === 'create' ? 'bg-gray-200/50 text-gray-900' : 'text-gray-600 hover:bg-gray-100/50 hover:text-gray-900'}`}
                 >
                   <Plus size={16} strokeWidth={2} className={activeView === 'create' ? 'text-gray-900' : 'text-gray-500'} />
                   Novo Portfólio
                 </button>
              </div>
            </aside>

            {/* Contextual Main Content */}
            <main className="flex-1 relative bg-[#FAFAFA] overflow-y-auto w-full h-full">
               <div className="absolute inset-0 bg-[#FAFAFA] bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:20px_20px] opacity-30 pointer-events-none"></div>
               <div className="relative z-10 min-h-full h-full">
                  {activeView === 'list' ? (
                    <PortfoliosList 
                      showcases={showcases} 
                      onNew={() => setActiveView('create')} 
                      onDelete={handleDelete}
                      onView={(slug) => { 
                         const s = showcases.find(x => x.slug === slug);
                         if (s) {
                            setEditShowcaseId(s.id); 
                            setActiveView('edit'); 
                         }
                      }}
                    />
                  ) : activeView === 'create' ? (
                    <ShowcaseBuilder 
                      clients={clients} 
                      onSave={handleSaveShowcase} 
                      onCancel={() => setActiveView('list')} 
                    />
                  ) : activeView === 'edit' && editShowcaseId ? (
                    <ShowcaseEditor
                       showcase={showcases.find(s => s.id === editShowcaseId)!}
                       clients={clients}
                       onSave={handleSaveShowcase}
                       onClose={() => setActiveView('list')}
                    />
                  ) : null}
               </div>
            </main>
          </>
        )}

        {/* MICRO-SYSTEM 2: PROPOSALS (MOCK) */}
        {activeSystem === 'proposals' && (
          <>
            {/* Contextual Sidebar */}
            <aside className="w-[220px] bg-[#FAFAFA] border-r border-gray-200 flex flex-col shrink-0 z-20">
              <div className="flex-1 py-6 px-4 flex flex-col gap-1 overflow-y-auto">
                 <button 
                   onClick={() => setProposalView('list')}
                   className={`flex items-center gap-2.5 px-3 py-2 rounded-md text-[14px] font-medium transition-all ${proposalView === 'list' ? 'bg-gray-200/50 text-gray-900' : 'text-gray-600 hover:bg-gray-100/50 hover:text-gray-900'}`}
                 >
                   <FileText size={16} strokeWidth={2} className={proposalView === 'list' ? 'text-gray-900' : 'text-gray-500'} />
                   Em Andamento
                 </button>
                 <button 
                   onClick={() => setProposalView('templates')}
                   className={`flex items-center gap-2.5 px-3 py-2 rounded-md text-[14px] font-medium transition-all ${proposalView === 'templates' ? 'bg-gray-200/50 text-gray-900' : 'text-gray-600 hover:bg-gray-100/50 hover:text-gray-900'}`}
                 >
                   <LayoutGrid size={16} strokeWidth={2} className={proposalView === 'templates' ? 'text-gray-900' : 'text-gray-500'} />
                   Templates
                 </button>
              </div>
            </aside>

            {/* Contextual Main Content */}
            <main className="flex-1 relative bg-[#FAFAFA] overflow-y-auto w-full h-full flex flex-col items-center justify-center">
               <div className="absolute inset-0 bg-[#FAFAFA] bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:20px_20px] opacity-30 pointer-events-none"></div>
               <div className="relative z-10 text-center max-w-sm mt-[-10%]">
                  <div className="w-20 h-20 bg-gray-100 rounded-3xl flex items-center justify-center mx-auto mb-6 border border-gray-200 shadow-sm">
                     <FileText size={32} strokeWidth={1.5} className="text-gray-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Módulo em Construção</h2>
                  <p className="text-sm text-gray-500 leading-relaxed">
                     Em breve um novo micro-sistema dedicado para geração, rastreamento e envio de propostas comerciais arquitetadas.
                  </p>
               </div>
            </main>
          </>
        )}

        {/* MICRO-SYSTEM 3: DOCUMENTS (MOCK) */}
        {activeSystem === 'documents' && (
          <main className="flex-1 relative bg-[#FAFAFA] overflow-y-auto w-full h-full flex flex-col items-center justify-center">
             <div className="absolute inset-0 bg-[#FAFAFA] bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:20px_20px] opacity-30 pointer-events-none"></div>
             <div className="relative z-10 text-center max-w-sm mt-[-10%]">
                <div className="w-20 h-20 bg-gray-100 rounded-3xl flex items-center justify-center mx-auto mb-6 border border-gray-200 shadow-sm">
                   <FileText size={32} strokeWidth={1.5} className="text-gray-400" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Área Limpa</h2>
                <p className="text-sm text-gray-500 leading-relaxed">
                   Todos os materiais e funcionalidades foram removidos conforme solicitado. O ambiente está limpo para recomeçar.
                </p>
             </div>
          </main>
        )}

      </div>
    </div>
  );
};
